import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import _startCase from 'lodash/startCase';

import { useGetTrqkInsightsForUserLazyQuery } from '@trqk/api';
import { locale, sumStatementHistoryData } from '@trqk/utils';
import { InsightsIndexLogo } from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';

import { useEntities } from '../../../hooks';
import { WebLayout } from '../../templates';
import {
    BumpFalloffCard,
    TopWritersCard,
    DetectionsByProductionCompanyCard,
    DetectionSummaryCard,
    DidYouKnowCard,
    TopPublishersCard,
    StatementHistoryCard,
    TopFiveProgramsCard,
    TourCards,
} from './components';

const { GET_SESSION_CURRENT_USER } = queryStrings;

export const Insights = (): JSX.Element => {
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id;
    const { selectedEntity, loading: loadingEntities } = useEntities();
    const [getTrqkInsightsForUser, getTrqkInsightsForUserQuery] =
        useGetTrqkInsightsForUserLazyQuery({
            fetchPolicy: 'cache-first',
            notifyOnNetworkStatusChange: true,
        });
    const selectedEntityId = selectedEntity?.id;
    const entityName = selectedEntity?.name ? (
        <span className="font-semibold">{_startCase(selectedEntity.name)}</span>
    ) : (
        locale.enUS.INSIGHTS.CARDS.CURRENT_ENTITY
    );

    const pro = selectedEntity?.pro ?? null;

    useEffect(() => {
        if (userId && !loadingEntities) {
            // We still want to call getTrqkInsightsForUser even if the selectedEntityId is
            // undefined, but we do not want to call it before this is confirmed by calling
            // getActiveEntities within the EntityProvider. Otherwise, multiple calls to
            // getTrqkInsightsForUser can result in a race condition where the last call to complete
            // will determine what gets rendered.
            getTrqkInsightsForUser({
                variables: {
                    id: userId,
                    entity_id: selectedEntityId,
                },
            });
        }
    }, [loadingEntities, selectedEntityId, userId]);

    const bumpFalloffReport =
        getTrqkInsightsForUserQuery?.data?.getTrqkInsightsForUser
            ?.bump_falloff_report ?? null;

    const topFiveProgramsData =
        getTrqkInsightsForUserQuery?.data?.getTrqkInsightsForUser
            ?.top_five_programs_user?.data ?? null;

    const lookupStatementHistoryData =
        getTrqkInsightsForUserQuery?.data?.getTrqkInsightsForUser
            ?.statement_history?.data ?? null;

    const statementHistoryData =
        lookupStatementHistoryData === null
            ? null
            : sumStatementHistoryData(lookupStatementHistoryData);

    const detectionSummaryData =
        getTrqkInsightsForUserQuery?.data?.getTrqkInsightsForUser
            ?.detections_summary?.data ?? null;

    const detectionsActive =
        getTrqkInsightsForUserQuery?.data?.getTrqkInsightsForUser
            ?.detections_summary?.detections_active ?? false;

    const productionCompanySummaryData =
        getTrqkInsightsForUserQuery?.data?.getTrqkInsightsForUser
            ?.production_company_summary?.data ?? null;

    const composersSummary =
        getTrqkInsightsForUserQuery?.data?.getTrqkInsightsForUser
            ?.composers_summary ?? null;

    const publishersSummary =
        getTrqkInsightsForUserQuery?.data?.getTrqkInsightsForUser
            ?.publishers_summary ?? null;

    const loading =
        loadingEntities ||
        !getTrqkInsightsForUserQuery.called ||
        getTrqkInsightsForUserQuery.loading;

    return (
        <WebLayout mainWidth="100%" loading={loading}>
            <h3 className="mt-20">{locale.enUS.INSIGHTS.TOUR_TITLE}</h3>
            <div className="text-tertiary-400 italic text-sm">
                {locale.enUS.INSIGHTS.TOUR_DESCRIPTION}
            </div>
            <div className="w-full mt-3 pr-8">
                <TourCards
                    detectionsActive={detectionsActive}
                    statementHistoryData={statementHistoryData}
                    queryCalled={getTrqkInsightsForUserQuery.called}
                    queryLoading={getTrqkInsightsForUserQuery.loading}
                    entityId={selectedEntityId ?? 'unknownEntity'}
                    userId={userId}
                />
            </div>
            <h3 className="mt-6">{locale.enUS.INSIGHTS.INSIGHTS_TITLE}</h3>
            <div className="text-tertiary-400 italic text-sm flex items-center">
                {locale.enUS.INSIGHTS.INSIGHTS_DESCRIPTION.PART_1}
                &nbsp;
                <InsightsIndexLogo showLightning={true} />
            </div>
            <div className="pt-3 pb-10 w-full flex flex-wrap">
                {selectedEntityId && statementHistoryData && (
                    <StatementHistoryCard
                        data={statementHistoryData}
                        entityName={entityName}
                    />
                )}
                {/*
                    `detectionsActive` means the user has uploaded one or more audio files for the
                    current entity. In this case, we want to show the DetectionSummaryCard, which
                    will show an empty message if there is no detectionSummaryData to display.
                */}

                {detectionsActive && (
                    <DetectionSummaryCard data={detectionSummaryData} />
                )}
                {selectedEntityId && bumpFalloffReport && (
                    <BumpFalloffCard
                        bumpFalloffReport={bumpFalloffReport}
                        pro={pro}
                    />
                )}
                {/*
                    Even though the pro, newer_statement_period and older_statement period look like
                    they are part of the BumpFalloffReport data only, they actually apply to the
                    TopFiveProgramsCard and the TopFiveEarningsPerPlayCard as well.
                */}
                {selectedEntityId && topFiveProgramsData && (
                    <TopFiveProgramsCard
                        data={topFiveProgramsData}
                        entityName={entityName}
                    />
                )}
                {productionCompanySummaryData && (
                    <DetectionsByProductionCompanyCard
                        data={productionCompanySummaryData}
                    />
                )}
                {composersSummary?.data && (
                    <TopWritersCard composersSummary={composersSummary} />
                )}
                {publishersSummary?.data && (
                    <TopPublishersCard publishersSummary={publishersSummary} />
                )}
                <DidYouKnowCard />
            </div>
        </WebLayout>
    );
};
